import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import QuestionCard from './components/QuestionCard';
import ScoreResult from './components/ScoreResult';

const questions = [
  {
    question: 'How would you describe the origin of your primary problem?',
    subtitle: 'Select the option that most accurately reflects your situation.',
    options: [
      { label: 'It appeared suddenly with no warning whatsoever', value: 6, tag: 'Acute' },
      { label: 'I created it myself through a series of confident decisions', value: 9, tag: 'Self-Inflicted' },
      { label: 'It evolved slowly from a smaller, ignored problem', value: 8, tag: 'Compounding' },
      { label: 'I inherited it from someone else\'s confident decisions', value: 7, tag: 'Legacy' },
      { label: 'I am not entirely sure it is a problem yet', value: 3, tag: 'Nascent' },
    ],
  },
  {
    question: 'How many people are currently aware of your problem?',
    subtitle: 'Awareness radius is a key severity multiplier in our 14-framework matrix.',
    options: [
      { label: 'Just me — I have been managing it quietly', value: 4, tag: 'Contained' },
      { label: 'A small circle of trusted confidants', value: 6, tag: 'Spreading' },
      { label: 'My entire department or household', value: 8, tag: 'Widespread' },
      { label: 'It has been discussed in meetings I was not invited to', value: 10, tag: 'Critical' },
      { label: 'It has its own nickname among colleagues', value: 10, tag: 'Legendary' },
    ],
  },
  {
    question: 'How many times have you attempted to solve this problem?',
    subtitle: 'Prior solution attempts are a strong indicator of problem resilience.',
    options: [
      { label: 'I have not tried yet — I am still processing', value: 3, tag: 'Pre-Action' },
      { label: 'Once or twice, with moderate optimism', value: 5, tag: 'Early Stage' },
      { label: 'Several times using different approaches', value: 7, tag: 'Persistent' },
      { label: 'Many times — each attempt made it slightly worse', value: 9, tag: 'Escalating' },
      { label: 'I have lost count and also some sleep', value: 10, tag: 'Advanced' },
    ],
  },
  {
    question: 'Does your problem have sub-problems?',
    subtitle: 'Nested problem architecture is our specialty and your misfortune.',
    options: [
      { label: 'No, it seems fairly straightforward', value: 2, tag: 'Underdeveloped' },
      { label: 'Yes, one or two minor complications', value: 5, tag: 'Developing' },
      { label: 'Yes, several — and they interact with each other', value: 8, tag: 'Complex' },
      { label: 'Yes, and the sub-problems have their own sub-problems', value: 10, tag: 'Fractal' },
      { label: 'I discovered a new sub-problem while filling out this form', value: 10, tag: 'Live Crisis' },
    ],
  },
  {
    question: 'How does your problem make you feel at 2:00 AM?',
    subtitle: 'Nocturnal problem resonance is a clinically significant severity indicator.',
    options: [
      { label: 'I sleep fine — it does not bother me at night', value: 1, tag: 'Concerning' },
      { label: 'Mildly restless, but I drift back off', value: 4, tag: 'Moderate' },
      { label: 'Wide awake, running through scenarios', value: 7, tag: 'Elevated' },
      { label: 'Fully alert, drafting emails I will not send', value: 9, tag: 'Severe' },
      { label: 'I have a dedicated 2 AM problem journal', value: 10, tag: 'Peak Severity' },
    ],
  },
  {
    question: 'Have you explained your problem to someone who then developed their own problem as a result?',
    subtitle: 'Problem transmissibility is a hallmark of truly exceptional complexity.',
    options: [
      { label: 'No, people seem fine after I explain it', value: 1, tag: 'Low Contagion' },
      { label: 'One person seemed a bit unsettled', value: 4, tag: 'Mild Spread' },
      { label: 'Yes, at least two people are now also worried', value: 7, tag: 'Contagious' },
      { label: 'Yes, and one of them stopped returning my calls', value: 9, tag: 'Virulent' },
      { label: 'My problem has been described as "a lot" by multiple parties', value: 10, tag: 'Epidemic' },
    ],
  },
  {
    question: 'How would you rate the documentation of your problem?',
    subtitle: 'Proper problem documentation is the foundation of sustainable complexity.',
    options: [
      { label: 'It exists only in my head', value: 5, tag: 'Undocumented' },
      { label: 'A few notes in a notebook I cannot find', value: 6, tag: 'Informal' },
      { label: 'A spreadsheet that has become its own problem', value: 8, tag: 'Documented' },
      { label: 'A full folder with tabs, timelines, and a glossary', value: 9, tag: 'Comprehensive' },
      { label: 'A shared drive with version history and contributor credits', value: 10, tag: 'Enterprise-Grade' },
    ],
  },
  {
    question: 'On a scale of urgency, where does your problem currently sit?',
    subtitle: 'Our urgency calibration uses a proprietary non-linear scale.',
    options: [
      { label: 'It can wait — probably', value: 2, tag: 'Deferred' },
      { label: 'It should be addressed soon-ish', value: 5, tag: 'Pending' },
      { label: 'It needs attention within the week', value: 7, tag: 'Pressing' },
      { label: 'It needed attention last month', value: 9, tag: 'Overdue' },
      { label: 'It needed attention before I was born', value: 10, tag: 'Generational' },
    ],
  },
];

const MAX_SCORE = questions.length * 10;

const ProblemSeverityCalculator = () => {
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(Array(questions.length).fill(null));
  const [showResult, setShowResult] = useState(false);
  const [started, setStarted] = useState(false);

  const handleSelect = (value: number) => {
    const updated = [...answers];
    updated[currentQ] = value;
    setAnswers(updated);
  };

  const handleNext = () => {
    if (currentQ < questions.length - 1) {
      setCurrentQ(currentQ + 1);
    } else {
      setShowResult(true);
    }
  };

  const handleBack = () => {
    if (currentQ > 0) setCurrentQ(currentQ - 1);
  };

  const handleRetake = () => {
    setAnswers(Array(questions.length).fill(null));
    setCurrentQ(0);
    setShowResult(false);
    setStarted(false);
  };

  const totalScore = answers.reduce((sum, a) => sum + (a ?? 0), 0);
  const currentAnswer = answers[currentQ];

  return (
    <div className="bg-black min-h-screen">
      <Navigation />

      {/* Hero */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f5a5fd675c49e53e9aecc792a9fa668d.jpg"
            alt="Dramatic sky"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-gold text-sm font-semibold tracking-widest uppercase mb-4 block">
              Proprietary Diagnostic Tool
            </span>
            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight font-serif">
              Problem Severity Calculator™
            </h1>
            <p className="text-xl text-cream/75 max-w-2xl mx-auto leading-relaxed">
              Our 14-framework diagnostic engine will assess the true depth, complexity, and alarming potential of your situation — and deliver a score that will concern you appropriately.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calculator Body */}
      <section className="py-20 bg-black">
        <div className="max-w-2xl mx-auto px-6">

          <AnimatePresence mode="wait">
            {!started && !showResult && (
              <motion.div
                key="intro"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="text-center"
              >
                <div className="bg-dark-gray/60 border border-gold/20 rounded-3xl p-12 mb-8">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gold/10 border border-gold/30 mx-auto mb-8">
                    <i className="ri-stethoscope-line text-4xl text-gold"></i>
                  </div>
                  <h2 className="text-3xl font-black text-white mb-4 font-serif">
                    Before We Begin
                  </h2>
                  <p className="text-cream/70 leading-relaxed mb-6">
                    This assessment consists of <strong className="text-white">8 carefully calibrated questions</strong> designed to measure the severity, complexity, and untapped potential of your current problem. Results are generated using our proprietary Problem Severity Matrix™ and are guaranteed to be alarming.
                  </p>
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    {[
                      { icon: 'ri-time-line', label: '3 Minutes', sub: 'Average completion' },
                      { icon: 'ri-question-answer-line', label: '8 Questions', sub: 'Scientifically selected' },
                      { icon: 'ri-alarm-warning-line', label: '100% Alarming', sub: 'Guaranteed results' },
                    ].map((item, i) => (
                      <div key={i} className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                          <i className={`${item.icon} text-gold text-xl`}></i>
                        </div>
                        <p className="text-white font-bold text-sm">{item.label}</p>
                        <p className="text-cream/50 text-xs mt-0.5">{item.sub}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-cream/40 text-xs mb-8 italic">
                    Disclaimer: All scores are deeply concerning regardless of the number displayed. Side effects may include heightened awareness of previously unnoticed problems, spontaneous sub-problem generation, and an urgent desire to schedule a consultation.
                  </p>
                  <button
                    onClick={() => setStarted(true)}
                    className="bg-gold text-black px-12 py-5 rounded-full font-black text-lg hover:bg-white transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Begin Diagnostic Assessment
                  </button>
                </div>
              </motion.div>
            )}

            {started && !showResult && (
              <motion.div
                key="questions"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="bg-dark-gray/60 border border-white/10 rounded-3xl p-10"
              >
                <AnimatePresence mode="wait">
                  <QuestionCard
                    key={currentQ}
                    question={questions[currentQ].question}
                    subtitle={questions[currentQ].subtitle}
                    options={questions[currentQ].options}
                    selected={currentAnswer}
                    onSelect={handleSelect}
                    questionNumber={currentQ + 1}
                    totalQuestions={questions.length}
                  />
                </AnimatePresence>

                <div className="flex items-center justify-between mt-10 pt-6 border-t border-white/10">
                  <button
                    onClick={handleBack}
                    disabled={currentQ === 0}
                    className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                      currentQ === 0
                        ? 'text-white/20 border border-white/10 cursor-not-allowed'
                        : 'text-cream border border-white/20 hover:border-gold hover:text-gold'
                    }`}
                  >
                    <i className="ri-arrow-left-line"></i>
                    Back
                  </button>

                  <button
                    onClick={handleNext}
                    disabled={currentAnswer === null}
                    className={`flex items-center gap-2 px-8 py-3 rounded-full font-black text-sm transition-all whitespace-nowrap cursor-pointer ${
                      currentAnswer !== null
                        ? 'bg-gold text-black hover:bg-white'
                        : 'bg-white/10 text-white/30 cursor-not-allowed'
                    }`}
                  >
                    {currentQ === questions.length - 1 ? 'Calculate My Severity' : 'Next Question'}
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </motion.div>
            )}

            {showResult && (
              <motion.div
                key="result"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-dark-gray/60 border border-white/10 rounded-3xl p-10"
              >
                <ScoreResult
                  score={totalScore}
                  maxScore={MAX_SCORE}
                  answers={answers as number[]}
                  onRetake={handleRetake}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProblemSeverityCalculator;
